// src/components/TagSelector.tsx
import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa6';
import { IMAGE_TAGS, ImageTag } from '../../../managers/ContentManager';

interface TagSelectorProps {
  /** which tag keys are currently selected */
  selectedTags: string[];
  /** callback when the selection changes */
  onChange: (updated: string[]) => void;
  /** which tag keys to show; defaults to all */
  availableTags?: string[];
  /** if true, wraps in a “Filter By Categories” toggle */
  collapsible?: boolean;
  /** override toggle button labels */
  toggleLabel?: { closed?: string; open?: string };
}

const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onChange,
  availableTags = IMAGE_TAGS.map(t => t.tag),
  collapsible = false,
  toggleLabel = {
    closed: 'Filter By Categories',
    open: 'Hide Filter By Categories',
  },
}) => {
  const [open, setOpen] = useState(!collapsible);

  // build our list of full ImageTag objects
  const tagsToShow: ImageTag[] = availableTags
    .map(tag => IMAGE_TAGS.find(t => t.tag === tag))
    .filter((t): t is ImageTag => !!t);

  const handleClick = (tag: string) => {
    onChange(
      selectedTags.includes(tag)
        ? selectedTags.filter(t => t !== tag)
        : [...selectedTags, tag]
    );
  };

  // the tag buttons laid out inline, wrapping to new lines as needed
  const content = (
    <div className='mb-2'
      style={{
        display: 'flex',
        flexWrap: 'wrap',
      }}
    >
      {tagsToShow.map(tagObj => {
        const Icon = tagObj.icon;
        const isSelected = selectedTags.includes(tagObj.tag);
        return (
          <Button
            key={tagObj.tag}
            variant={isSelected ? 'primary' : 'outline-primary'}
            onClick={() => handleClick(tagObj.tag)}
            className="d-flex align-items-center px-3 mb-2 me-2"
            style={{
              whiteSpace: 'nowrap',
              fontSize: '0.9rem',
              WebkitTapHighlightColor: 'transparent'
            }}
            onTouchEnd={(e) => {
              e.currentTarget.blur();
            }}
          >
            <Icon className="me-2" />
            {tagObj.label}
          </Button>
        );
      })}
    </div>
  );

  // if collapsible, wrap in a toggle
  if (collapsible) {
    return (
      <div>
        <Button
          variant="secondary"
          className="mb-2 w-100"
          onClick={() => setOpen(o => !o)}
        >
          {open ? toggleLabel.open : toggleLabel.closed}
          {open ? <FaChevronDown className="ms-2" /> : <FaChevronRight className="ms-2" />}
        </Button>
        {open && content}
      </div>
    );
  }

  return content;
};

export default TagSelector;
